import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useProfileStore } from '../store/profileStore';
import { useNavigate } from 'react-router-dom';
import { ProfileModeToggles } from './profile/ProfileModeToggles';

interface UserProfileDialogProps {
  onClose: () => void;
}

export function UserProfileDialog({ onClose }: UserProfileDialogProps) {
  const { hormoneType, setHormoneType, resetProfile } = useProfileStore();
  const navigate = useNavigate();

  const handleReset = () => {
    resetProfile();
    navigate('/', { replace: true });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-start justify-center pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="profile-dialog bg-background rounded-xl border border-border max-w-sm w-full mx-4 overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold">Profile Settings</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Hormone Profile */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Hormone Profile</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setHormoneType('testosterone')}
                  className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                    hormoneType === 'testosterone'
                      ? 'bg-gradient-to-br from-purple-800/20 via-purple-600/20 to-indigo-500/20 text-purple-400'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  <span>Testosterone</span>
                </button>
                <button
                  onClick={() => setHormoneType('estrogen')}
                  className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                    hormoneType === 'estrogen'
                      ? 'bg-gradient-to-br from-purple-500/20 to-amber-500/20 text-amber-500'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  <span>Estrogen</span>
                </button>
              </div>
            </div>

            {/* App Mode Toggles */}
            <ProfileModeToggles />

            {/* Reset Profile Button */}
            <button
              onClick={handleReset}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            >
              <X className="w-4 h-4" />
              Reset Profile
            </button>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <button
              onClick={onClose}
              className="w-full theme-button py-2 rounded-lg text-sm"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}